import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {
  Conversation,
  ConversationUpdateReason,
  Message,
  MessageUpdateReason,
  Paginator,
  Participant,
  ParticipantUpdateReason,
  User
} from "@twilio/conversations";
import {ChatService} from "../../services/chat.service";
import {ChatHttpService} from "../../services/chat-http.service";
import {CurrentSiteContextService} from "../../../commons/angular/services/current-site-context.service";

require('./chat-conversations-list-item.scss');
@Component({
  standalone: false,
  selector: 'chat-conversations-list-item',
  templateUrl: './chat-conversations-list-item.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatConversationsListItemComponent implements OnInit, OnDestroy {

  @Input() conversation: Conversation;
  @Output() onSelect: EventEmitter<Conversation> = new EventEmitter();
  @Output() onMessageAdded: EventEmitter<Conversation> = new EventEmitter();

  public color: string;
  public currentUser: User;
  public lastMessage: Message;
  public lastMessageUser: User;
  public conversationUsers: User[] = [];
  public unreadMessagesCount: number;
  public participantTyping: Participant;

  private siteId: string;

  constructor(@Inject(ChatService) private chatService: ChatService,
              @Inject(ChatHttpService) private chatHttpService: ChatHttpService,
              @Inject(CurrentSiteContextService) private currentSiteContextService: CurrentSiteContextService,
              @Inject(ChangeDetectorRef) private changeDetectorRef: ChangeDetectorRef) {

  }

  ngOnInit(): void {
    this.siteId = this.currentSiteContextService.currentSiteId;
    this.currentUser = this.chatService.getCurrentUser();
    this.color = this.chatService.getColorById(this.conversation.sid);
    this._getParticipants();
    this._getLastMessage();
    this._getUnreadMessagesCount();
    this._initConversationSubscriptions();
  }

  ngOnDestroy(): void {
    this.conversation.removeAllListeners();
  }

  onSelectClick(){
    this.onSelect.emit(this.conversation);
  }

  onUnmuteCurrentUser() {
    this.chatHttpService.muteConversationUsers(
      this.siteId,
      this.conversation.sid,
      [],
      [this.currentUser.identity]
    )
      .subscribe(() => {
        this._checkDetector();
      })
  }

  onMuteCurrentUser() {
    this.chatHttpService.muteConversationUsers(
      this.siteId,
      this.conversation.sid,
      [this.currentUser.identity],
      []
    )
      .subscribe(() => {
        this._checkDetector();
      })
  }

  private _getParticipants() {
    this.conversation.getParticipants()
      .then((participants: Participant[]) => {
        participants.forEach((p: Participant) => {
          p.getUser()
            .then((user: User) => {
              if (this.currentUser.identity !== user.identity) {
                this.conversationUsers = this.conversationUsers.concat([user]);
                this._checkDetector();
              }
            })
        })
      })
  }

  private _getLastMessage() {
    this.conversation.getMessages(1)
      .then((messagesPaginator: Paginator<Message>) => {
        this.lastMessage = messagesPaginator.items[0];

        this._checkDetector();
        if (this.lastMessage) {
          this._getLastMessageUser(this.lastMessage);
        }
      })
  }

  private _getLastMessageUser(message: Message) {
    message.getParticipant()
      .then((participant: Participant) => {
        // this.participant = participant;

        participant.getUser()
          .then((user: User) => {
            this.lastMessageUser = user;
            this._checkDetector();
          })
      })
  }

  private _getUnreadMessagesCount() {
    this.unreadMessagesCount = !!this.conversation.lastMessage ?
      (this.conversation.lastReadMessageIndex === null ? (this.conversation.lastMessage.index + 1) : (this.conversation.lastMessage.index - this.conversation.lastReadMessageIndex)) : 0;
  }

  private _initConversationSubscriptions() {
    this.conversation.on("messageAdded", (message: Message) => {
      if (this.conversation.notificationLevel !== 'muted') {
        this.unreadMessagesCount = this.unreadMessagesCount + 1;
      }

      this.lastMessage = message;
      this._getLastMessageUser(message);

      this.onMessageAdded.emit(this.conversation);
      this._checkDetector();
    });
    this.conversation.on("messageRemoved", (message: Message) => {
      this._checkDetector();
    });
    this.conversation.on("messageUpdated", (data: {message: Message, updateReasons: MessageUpdateReason[]}) => {
      this._checkDetector();
    });
    this.conversation.on("participantJoined", (participant: Participant) => {
      this._checkDetector();
    });
    this.conversation.on("participantLeft", (participant: Participant) => {
      this._checkDetector();
    });
    this.conversation.on("participantUpdated", (data: {participant: Participant, updateReasons: ParticipantUpdateReason[]}) => {
      this._checkDetector();
    });
    this.conversation.on("removed", (conversation: Conversation) => {
      this._checkDetector();
    });
    this.conversation.on("typingStarted", (participant: Participant) => {
      this.participantTyping = participant;
      this._checkDetector();
    });
    this.conversation.on("typingEnded", (participant: Participant) => {
      if (this.participantTyping.sid === participant.sid) {
        this.participantTyping = undefined;
        this._checkDetector();
      }
    });
    this.conversation.on("updated", (data: {conversation: Conversation, updateReasons: ConversationUpdateReason[]}) => {
      this._getUnreadMessagesCount();
      this._checkDetector();
    });
  }

  private _checkDetector() {
    this.changeDetectorRef.detectChanges();
  }

}
